import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAddToast } from '@hm-group/bads-react';
import { environment } from '../environments/environment';
import { useAppContext } from '../context/AppContext';
import { httpGet, httpPut } from '../services/httpService';
import type {
  Market,
  DepartmentMetadata,
  SectionMetadata,
  DepartmentMetadataResponse,
  SectionMetadataResponse,
} from '../models';
import {
  ChannelType,
  ScaActionType,
  MARKET_API,
  METADATA_DEPARTMENT_API,
  METADATA_SECTION_API
} from '../models';
import type { MarketListItem, MarketRegionConfig, SelectionBarState } from '../components/SelectionBar/SelectionBar';
import { useScaModal } from './useScaModal';
import type { ScaAction } from './useScaModal';
import { useMarketModal } from './useMarketModal';
import type { MarketLinkAction } from './useMarketModal';
import { computeKpiSelection } from '../utils/kpiSelectionUtils';
import { buildMarketList, getMainMarketName, getMainMarketCode } from '../utils/marketUtils';

// Re-export so existing consumers keep working
export type { KpiSelectionResult } from '../utils/kpiSelectionUtils';

// ─── Hook ──────────────────────────────────────────────────────────
const ONLINE_SCA_ERROR_TITLE = 'Data Integrity Error';
const ONLINE_SCA_ERROR_SUBTITLE =
  'Online channel should not have Core/Extended, Excitement, or Include Excitement enabled. These values have been reset to off.';

export function useSelectionBarLogic() {
  const { searchBarData } = useAppContext();
  const { addToast } = useAddToast();
  const apiUri = environment.apiConfig.articleplan_api.uri;

  // Track previous values to detect changes (mirrors Angular's previousSelectedBrand etc.)
  const prevBrandRef = useRef<string | undefined>(undefined);
  const prevDepartmentRef = useRef<string | undefined>(undefined);
  const prevSectionRef = useRef<string | undefined>(undefined);

  // Raw market data from API
  const [markets, setMarkets] = useState<Market[]>([]);

  // Department metadata
  const [departmentMetadata, setDepartmentMetadata] = useState<DepartmentMetadata>({
    departmentId: 0,
    channelId: 0,
    isCoreExtendedChecked: false,
    isExcitementChecked: false,
    includeExcitementInSummary: false,
    selectedMarkets: [],
    visibleMarkets: [],
    stateGuid: '',
  });

  // Section metadata
  const [sectionMetadata, setSectionMetadata] = useState<SectionMetadata>({
    sectionId: 0,
    departmentId: 0,
    channelId: 0,
    isCoreExtendedChecked: false,
    isExcitementChecked: false,
    isPlannedArticlesChecked: false,
    includeExcitementInSummary: false,
    selectedMarkets: [],
    visibleMarkets: [],
    stateGuid: '',
  });

  // UI state
  const [isStoreTabSelected, setIsStoreTabSelected] = useState(true);
  const [isShowGroundArticlesToggleChecked, setIsShowGroundArticlesToggleChecked] = useState(false);
  const [isIncludeExcitementToggleChecked, setIsIncludeExcitementToggleChecked] = useState(false);
  const [selectedView, setSelectedView] = useState<'department' | 'section'>('department');

  // Market list state for UI - tracks visibility (checked) independently from linked state
  const [storeMarketsList, setStoreMarketsList] = useState<MarketListItem[]>([]);
  const [onlineMarketsList, setOnlineMarketsList] = useState<MarketListItem[]>([]);

  const channel = isStoreTabSelected ? ChannelType.Store : ChannelType.Online;

  // ─── Derived values from searchBarData ──────────────────────────
  const isDepartmentSelected = searchBarData.showWorkspace;
  const isSectionSelected =
    Boolean(searchBarData.section) && searchBarData.section !== '';
  const currentDepartmentId =
    searchBarData.departments?.[0] ?? undefined;

  // ─── Fetch markets for brand ────────────────────────────────────
  const fetchMarkets = useCallback(
    async (brandId: string) => {
      try {
        const data = await httpGet<Market[]>(
          `${apiUri}${MARKET_API}${brandId}`,
        );
        setMarkets(data);
      } catch {
        setMarkets([]);
      }
    },
    [apiUri],
  );

  // ─── Load department metadata ───────────────────────────────────
  const loadDepartmentMetadata = useCallback(
    async (deptId: string, channelType: ChannelType) => {
      try {
        const resp = await httpGet<DepartmentMetadataResponse>(
          `${apiUri}${METADATA_DEPARTMENT_API}${deptId}/${channelType}`,
        );
        const hasInvalidOnlineFlags =
          channelType === ChannelType.Online &&
          (resp.isCoreExtendedChecked || resp.isExcitementChecked || resp.includeExcitementInSummary);

        if (hasInvalidOnlineFlags) {
          addToast({ title: ONLINE_SCA_ERROR_TITLE, subtitle: ONLINE_SCA_ERROR_SUBTITLE, type: 'error', timeout: 5000 });
        }

        const forceOffline = channelType === ChannelType.Online;
        setDepartmentMetadata({
          departmentId: resp.departmentId,
          channelId: channelType,
          isCoreExtendedChecked: forceOffline ? false : resp.isCoreExtendedChecked,
          isExcitementChecked: forceOffline ? false : resp.isExcitementChecked,
          includeExcitementInSummary: forceOffline ? false : resp.includeExcitementInSummary,
          selectedMarkets: resp.selectedMarkets,
          visibleMarkets: resp.visibleMarkets ?? resp.selectedMarkets,
          stateGuid: resp.stateGuid,
        });
        setIsIncludeExcitementToggleChecked(forceOffline ? false : resp.includeExcitementInSummary);
      } catch {
        // API call failed – keep current state
      }
    },
    [apiUri, addToast],
  );

  // ─── Load section metadata ──────────────────────────────────────
  const loadSectionMetadata = useCallback(
    async (sectionId: string, channelType: ChannelType) => {
      try {
        const resp = await httpGet<SectionMetadataResponse>(
          `${apiUri}${METADATA_SECTION_API}${sectionId}/${channelType}`,
        );
        const isCoreExtendedChecked = resp.metadata.some(
          (item) => item.isCoreExtendedChecked,
        );
        const isExcitementChecked = resp.metadata.some(
          (item) => item.isExcitementChecked,
        );
        const isPlannedArticlesChecked = resp.metadata.some(
          (item) => item.isPlannedArticlesChecked,
        );
        const includeExcitement = resp.metadata.some(
          (item) => item.includeExcitementInSummary,
        );
        const distinctMarkets = resp.metadata
          .flatMap((item) => item.selectedMarkets ?? [])
          .filter(
            (market, index, self) =>
              index === self.findIndex((m) => m.marketCode === market.marketCode),
          );

        const forceOffline = channelType === ChannelType.Online;
        const hasInvalidOnlineFlags =
          forceOffline && (isCoreExtendedChecked || isExcitementChecked || includeExcitement);

        if (hasInvalidOnlineFlags) {
          addToast({ title: ONLINE_SCA_ERROR_TITLE, subtitle: ONLINE_SCA_ERROR_SUBTITLE, type: 'error', timeout: 5000 });
        }

        setSectionMetadata({
          sectionId: resp.sectionId,
          departmentId: resp.metadata[0]?.departmentId ?? 0,
          channelId: channelType,
          isCoreExtendedChecked: forceOffline ? false : isCoreExtendedChecked,
          isExcitementChecked: forceOffline ? false : isExcitementChecked,
          isPlannedArticlesChecked,
          includeExcitementInSummary: forceOffline ? false : includeExcitement,
          selectedMarkets: distinctMarkets,
          visibleMarkets: resp.metadata
            .flatMap((item) => item.visibleMarkets ?? [])
            .filter(
              (market, index, self) =>
                index === self.findIndex((m) => m.marketCode === market.marketCode),
            ),
          stateGuid: resp.metadata[0]?.stateGuid ?? '',
        });
        setIsIncludeExcitementToggleChecked(forceOffline ? false : includeExcitement);
      } catch {
        // API call failed
      }
    },
    [apiUri, addToast],
  );

  // ─── React to searchBarData changes (mirrors Angular's reactToSearchBarChanges) ──
  useEffect(() => {
    const brandChanged = searchBarData.brand !== prevBrandRef.current;
    const deptChanged =
      currentDepartmentId !== prevDepartmentRef.current;
    const sectionChanged = searchBarData.section !== prevSectionRef.current;

    // Brand changed → reset metadata + fetch markets
    if (brandChanged && searchBarData.brand) {
      setDepartmentMetadata((prev) => ({
        ...prev,
        departmentId: 0,
        channelId: 0,
        isCoreExtendedChecked: false,
        isExcitementChecked: false,
        includeExcitementInSummary: false,
        selectedMarkets: [],
        visibleMarkets: [],
        stateGuid: '',
      }));
      setSectionMetadata((prev) => ({
        ...prev,
        sectionId: 0,
        departmentId: 0,
        channelId: 0,
        isCoreExtendedChecked: false,
        isExcitementChecked: false,
        isPlannedArticlesChecked: false,
        includeExcitementInSummary: false,
        selectedMarkets: [],
        visibleMarkets: [],
        stateGuid: '',
      }));
      setMarkets([]);
      fetchMarkets(searchBarData.brand);
    }

    // Department changed → load department metadata (only in department view)
    if (deptChanged && isDepartmentSelected && currentDepartmentId && selectedView === 'department') {
      loadDepartmentMetadata(currentDepartmentId, channel);
    }

    // Section changed → load section metadata (only in section view)
    if (sectionChanged && searchBarData.section && selectedView === 'section') {
      loadSectionMetadata(searchBarData.section, channel);
    }

    // Section cleared and no department → reset to department view
    if (
      searchBarData.section === undefined &&
      searchBarData.departments?.length === 0
    ) {
      setSelectedView('department');
    }

    // Update refs
    prevBrandRef.current = searchBarData.brand;
    prevDepartmentRef.current = currentDepartmentId;
    prevSectionRef.current = searchBarData.section;
  }, [
    searchBarData,
    channel,
    currentDepartmentId,
    isDepartmentSelected,
    fetchMarkets,
    loadDepartmentMetadata,
    loadSectionMetadata,
  ]);

  // ─── Reload metadata when tab (channel) or view (ButtonGroup) changes ──
  useEffect(() => {
    if (selectedView === 'section' && searchBarData.section) {
      loadSectionMetadata(searchBarData.section, channel);
    } else if (selectedView === 'department' && isDepartmentSelected && currentDepartmentId) {
      loadDepartmentMetadata(currentDepartmentId, channel);
    }
    // Only run when channel or view changes – not on every searchBarData change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, selectedView]);

  // ─── Build market lists from raw data + metadata ─────────────
  const activeMetadata =
    selectedView === 'section' ? sectionMetadata : departmentMetadata;
  
  // Stable dependency for useEffect - memoize based on the actual metadata reference
  const selectedMarketCodesKey = useMemo(
    () => activeMetadata.selectedMarkets.map((m) => m.marketCode).join(','),
    [activeMetadata.selectedMarkets],
  );

  const visibleMarketCodesKey = useMemo(
    () => activeMetadata.visibleMarkets.map((m) => m.marketCode).join(','),
    [activeMetadata.visibleMarkets],
  );

  // Memoize markets key for stable comparison
  const marketsKey = useMemo(
    () => markets.map((m) => `${m.marketCode}-${m.isMainMarket}`).join(','),
    [markets],
  );

  // ─── Rebuild market lists when markets or metadata actually changes ─────
  useEffect(() => {
    if (markets.length === 0) return; // Don't rebuild if no markets loaded yet
    
    const selectedCodes = activeMetadata.selectedMarkets.map((m) => m.marketCode);
    const visibleCodes = activeMetadata.visibleMarkets.length > 0
      ? activeMetadata.visibleMarkets.map((m) => m.marketCode)
      : undefined; // undefined → buildMarketList falls back to selectedCodes
    const storeList = buildMarketList(markets, ChannelType.Store, selectedCodes, visibleCodes);
    const onlineList = buildMarketList(markets, ChannelType.Online, selectedCodes, visibleCodes);
    setStoreMarketsList(storeList);
    setOnlineMarketsList(onlineList);
  }, [marketsKey, selectedMarketCodesKey, visibleMarketCodesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Compute disabled states ──────────────────────────────────
  const storeMainMarketName = getMainMarketName(markets, ChannelType.Store);
  const onlineMainMarketName = getMainMarketName(markets, ChannelType.Online);
  const storeMainMarketCode = getMainMarketCode(markets, ChannelType.Store);
  const onlineMainMarketCode = getMainMarketCode(markets, ChannelType.Online);
  const mainMarketName = isStoreTabSelected
    ? storeMainMarketName
    : onlineMainMarketName;

  const noMainMarket = mainMarketName === '';
  const isSectionViewWithSection =
    selectedView === 'section' && isSectionSelected;
  const isDeptOnlineOnly = isDepartmentSelected && !isStoreTabSelected;

  const scaDisabled =
    noMainMarket ||
    isSectionViewWithSection ||
    isDeptOnlineOnly ||
    (!isDepartmentSelected && !isSectionViewWithSection);
  const marketDisabled =
    noMainMarket ||
    isSectionViewWithSection ||
    (!isDepartmentSelected && !isSectionViewWithSection);

  // ─── Compute KPI selection from metadata checkbox state ─────────
  const isPlannedArticlesChecked = selectedView === 'section'
    ? sectionMetadata.isPlannedArticlesChecked
    : false; // Department view never has planned articles

  // ─── SCA action dispatch state ──────────────────────────────────
  const [pendingScaAction, setPendingScaAction] = useState<ScaAction | null>(null);
  const [pendingMarketAction, setPendingMarketAction] = useState<MarketLinkAction | null>(null);

  const dispatchScaAction = useCallback((action: ScaAction) => {
    setPendingScaAction(action);
  }, []);

  const clearScaAction = useCallback(() => {
    setPendingScaAction(null);
  }, []);

  const dispatchMarketAction = useCallback((action: MarketLinkAction) => {
    setPendingMarketAction(action);
  }, []);

  const clearMarketAction = useCallback(() => {
    setPendingMarketAction(null);
  }, []);

  // ─── Stable setter callbacks for SCA modal hook ─────────────────
  const setCoreExtendedChecked = useCallback((val: boolean) => {
    setDepartmentMetadata((prev) => ({ ...prev, isCoreExtendedChecked: val }));
  }, []);

  const setExcitementChecked = useCallback((val: boolean) => {
    setDepartmentMetadata((prev) => ({ ...prev, isExcitementChecked: val }));
  }, []);

  const setIncludeExcitementToggle = useCallback((val: boolean) => {
    setIsIncludeExcitementToggleChecked(val);
    setDepartmentMetadata((prev) => ({ ...prev, includeExcitementInSummary: val }));
  }, []);

  const getMetadata = useCallback(
    () => (selectedView === 'section' ? { ...departmentMetadata, ...sectionMetadata } : departmentMetadata),
    [selectedView, departmentMetadata, sectionMetadata],
  );

  // ─── Compose SCA modal hook ─────────────────────────────────────
  const {
    scaModalConfig,
    handleCoreCheckboxClick,
    handleExcitementCheckboxClick,
  } = useScaModal({
    getMetadata,
    setCoreExtendedChecked,
    setExcitementChecked,
    setIncludeExcitementToggle,
    onScaAction: dispatchScaAction,
  });

  // ─── Compose market modal hook ──────────────────────────────────
  const {
    marketModalConfig,
    openMarketLinkModal,
  } = useMarketModal({
    onConfirmAction: dispatchMarketAction,
  });

  const kpiSelection = useMemo(
    () => computeKpiSelection(
      activeMetadata.isCoreExtendedChecked,
      activeMetadata.isExcitementChecked,
      isPlannedArticlesChecked,
      isIncludeExcitementToggleChecked,
    ),
    [
      activeMetadata.isCoreExtendedChecked,
      activeMetadata.isExcitementChecked,
      isPlannedArticlesChecked,
      isIncludeExcitementToggleChecked,
    ],
  );

  // ─── Handlers ───────────────────────────────────────────────────
  // Core/Extended and Excitement changes now go through the SCA modal hook
  const handleCoreExtendedChange = useCallback(
    (checked: boolean) => {
      handleCoreCheckboxClick(checked);
    },
    [handleCoreCheckboxClick],
  );

  const handleExcitementChange = useCallback(
    (checked: boolean) => {
      handleExcitementCheckboxClick(checked);
    },
    [handleExcitementCheckboxClick],
  );

  const handleIncludeExcitementToggleChange = useCallback(
    (checked: boolean) => {
      setIsIncludeExcitementToggleChecked(checked);
      setDepartmentMetadata((prev) => ({
        ...prev,
        includeExcitementInSummary: checked,
      }));
      dispatchScaAction({
        scaActionType: ScaActionType.IncludeExcitementInSummary,
        isCoreExtendedChecked: activeMetadata.isCoreExtendedChecked,
        isExcitementChecked: activeMetadata.isExcitementChecked,
        includeExcitementInSummary: checked,
      });
    },
    [dispatchScaAction, activeMetadata.isCoreExtendedChecked, activeMetadata.isExcitementChecked],
  );

  const handleShowGroundArticlesToggleChange = (checked: boolean) => {
    setIsShowGroundArticlesToggleChecked(checked);
  };

  // ─── Persist visibility to backend ──────────────────────────────
  const persistVisibility = useCallback(
    (updatedList: MarketListItem[], channelType: ChannelType) => {
      if (!currentDepartmentId) return;
      const visibleMarkets = markets.filter((m) =>
        m.channelIntegrationKey === channelType &&
        updatedList.some((item) => item.visible && item.value === m.marketCode.toString()),
      );
      httpPut<{ channelId: ChannelType; visibleMarkets: Market[] }, unknown>(
        `${apiUri}${METADATA_DEPARTMENT_API}${currentDepartmentId}/visible-markets`,
        { channelId: channelType, visibleMarkets },
      ).then(() => {
        setDepartmentMetadata((prev) => ({ ...prev, visibleMarkets }));
      }).catch(() => {
        addToast({ title: 'Save failed', subtitle: 'Could not save market visibility. Please try again.', type: 'error', timeout: 5000 });
      });
    },
    [currentDepartmentId, markets, apiUri, addToast],
  );

  // ─── Eye icon handlers (visibility toggle) ──────────────────────
  const handleStoreMarketEyeClick = useCallback(
    (market: MarketListItem) => {
      if (market.isMainMarket) return; // main market always visible
      setStoreMarketsList((prev) => {
        const updated = prev.map((m) =>
          m.value === market.value ? { ...m, visible: !m.visible } : m,
        );
        persistVisibility(updated, ChannelType.Store);
        return updated;
      });
    },
    [persistVisibility],
  );

  const handleOnlineMarketEyeClick = useCallback(
    (market: MarketListItem) => {
      if (market.isMainMarket) return; // main market always visible
      setOnlineMarketsList((prev) => {
        const updated = prev.map((m) =>
          m.value === market.value ? { ...m, visible: !m.visible } : m,
        );
        persistVisibility(updated, ChannelType.Online);
        return updated;
      });
    },
    [persistVisibility],
  );

  // ─── Link icon handlers (link/unlink region) ────────────────────
  const handleStoreMarketLinkClick = useCallback(
    (market: MarketListItem) => {
      if (market.isMainMarket) return;

      if (market.unlinked) {
        // Unlinked (independent data) → connect: show connect confirmation modal
        openMarketLinkModal(market, storeMainMarketName, storeMainMarketCode, 'connect');
      } else {
        // Linked (mirrors main) → disconnect: show disconnect confirmation modal
        openMarketLinkModal(market, storeMainMarketName, storeMainMarketCode, 'disconnect');
      }
    },
    [openMarketLinkModal, storeMainMarketName, storeMainMarketCode],
  );

  const handleOnlineMarketLinkClick = useCallback(
    (market: MarketListItem) => {
      if (market.isMainMarket) return;

      if (market.unlinked) {
        // Unlinked (independent data) → connect: show connect confirmation modal
        openMarketLinkModal(market, onlineMainMarketName, onlineMainMarketCode, 'connect');
      } else {
        // Linked (mirrors main) → disconnect: show disconnect confirmation modal
        openMarketLinkModal(market, onlineMainMarketName, onlineMainMarketCode, 'disconnect');
      }
    },
    [openMarketLinkModal, onlineMainMarketName, onlineMainMarketCode],
  );

  // ─── Assemble SelectionBarState ─────────────────────────────────
  const marketRegions: MarketRegionConfig[] = useMemo(() => [
    {
      channelLabel: 'Store region',
      name: 'storeRegion',
      testId: 'store-region-select',
      disabled: marketDisabled || !isStoreTabSelected,
      mainMarketName: storeMainMarketName,
      markets: storeMarketsList,
      onEyeClick: handleStoreMarketEyeClick,
      onLinkClick: handleStoreMarketLinkClick,
    },
    {
      channelLabel: 'Online region',
      name: 'onlineRegion',
      testId: 'online-region-select',
      disabled: marketDisabled || isStoreTabSelected,
      mainMarketName: onlineMainMarketName,
      markets: onlineMarketsList,
      onEyeClick: handleOnlineMarketEyeClick,
      onLinkClick: handleOnlineMarketLinkClick,
    },
  ], [
    marketDisabled, isStoreTabSelected,
    storeMainMarketName, storeMarketsList, handleStoreMarketEyeClick, handleStoreMarketLinkClick,
    onlineMainMarketName, onlineMarketsList, handleOnlineMarketEyeClick, handleOnlineMarketLinkClick,
  ]);

  const selectionBarState: SelectionBarState = {
    isCoreExtendedChecked: activeMetadata.isCoreExtendedChecked,
    isCoreExtendedDisabled: scaDisabled,
    isExcitementChecked: activeMetadata.isExcitementChecked,
    isExcitementDisabled: scaDisabled,
    isIncludeExcitementToggleChecked,
    isShowGroundArticlesToggleChecked,
    marketRegions,
    selectedView,
  };

  // ─── Handle confirmed market link action from modal ──────────────
  // pendingMarketAction is dispatched to DepartmentRegion which calls the API.
  // After API success, DepartmentRegion invokes handleMarketLinkCompleted to update unlinked status.

  // ─── Update unlinked status after DepartmentRegion API success ──────
  const handleMarketLinkCompleted = useCallback(
    (marketCode: number, actionType: 'connect' | 'disconnect') => {
      const marketCodeStr = marketCode.toString();
      // unlinked: true = independent data (disconnected from main), editable
      // unlinked: false = mirrors main (connected to main), read-only
      const isUnlinked = actionType === 'disconnect';
      const isStore = storeMarketsList.some((m) => m.value === marketCodeStr);

      if (isStore) {
        setStoreMarketsList((prev) =>
          prev.map((m) =>
            m.value === marketCodeStr ? { ...m, unlinked: isUnlinked } : m,
          ),
        );
      } else {
        setOnlineMarketsList((prev) =>
          prev.map((m) =>
            m.value === marketCodeStr ? { ...m, unlinked: isUnlinked } : m,
          ),
        );
      }

      // Keep departmentMetadata.selectedMarkets in sync so rebuilds from
      // visibility changes don't revert the unlinked state.
      // Backend semantics: market IN selectedMarkets = disconnected (has independent data).
      setDepartmentMetadata((prev) => {
        const alreadySelected = prev.selectedMarkets.some((m) => m.marketCode === marketCode);
        if (isUnlinked && !alreadySelected) {
          // Disconnect: add to selectedMarkets (market now has independent data)
          const freshMarket = markets.find((m) => m.marketCode === marketCode);
          if (freshMarket) {
            return { ...prev, selectedMarkets: [...prev.selectedMarkets, freshMarket] };
          }
        }
        if (!isUnlinked && alreadySelected) {
          // Connect: remove from selectedMarkets (independent data deleted)
          return { ...prev, selectedMarkets: prev.selectedMarkets.filter((m) => m.marketCode !== marketCode) };
        }
        return prev;
      });
    },
    [storeMarketsList, onlineMarketsList, markets],
  );

  // ─── General callback for when selectedMarkets changes from DepartmentRegion ──
  const handleSelectedMarketsChanged = useCallback(
    (selectedMarkets: DepartmentMetadata['selectedMarkets']) => {
      setDepartmentMetadata((prev) => ({ ...prev, selectedMarkets }));
    },
    [],
  );

  const handleViewChange = (view: 'department' | 'section') => {
    setSelectedView(view);
    if (view === 'section' && searchBarData.section) {
      loadSectionMetadata(searchBarData.section, channel);
    } else if (
      view === 'department' &&
      isDepartmentSelected &&
      currentDepartmentId
    ) {
      loadDepartmentMetadata(currentDepartmentId, channel);
    }
  };

  const handleTabChange = (tabIndex: number) => {
    setIsStoreTabSelected(tabIndex === 0);
  };

  // ─── Compute visible markets based on eye icon (visible) state ────
  // These are the markets that should show grids in DepartmentRegion
  const visibleStoreMarkets = useMemo(() => {
    const visibleCodes = new Set(
      storeMarketsList
        .filter((m) => m.visible)
        .map((m) => Number.parseInt(m.value, 10)),
    );
    return markets.filter(
      (m) => m.channelIntegrationKey === ChannelType.Store && visibleCodes.has(m.marketCode),
    );
  }, [storeMarketsList, markets]);

  const visibleOnlineMarkets = useMemo(() => {
    const visibleCodes = new Set(
      onlineMarketsList
        .filter((m) => m.visible)
        .map((m) => Number.parseInt(m.value, 10)),
    );
    return markets.filter(
      (m) => m.channelIntegrationKey === ChannelType.Online && visibleCodes.has(m.marketCode),
    );
  }, [onlineMarketsList, markets]);

  // ─── Connected market codes (unlinked=false, non-main markets) ──────
  // These markets mirror main market data and should be displayed as read-only.
  const storeConnectedMarketCodes = useMemo(() => {
    return new Set(
      storeMarketsList
        .filter((m) => !m.unlinked && !m.isMainMarket)
        .map((m) => Number.parseInt(m.value, 10)),
    );
  }, [storeMarketsList]);

  const onlineConnectedMarketCodes = useMemo(() => {
    return new Set(
      onlineMarketsList
        .filter((m) => !m.unlinked && !m.isMainMarket)
        .map((m) => Number.parseInt(m.value, 10)),
    );
  }, [onlineMarketsList]);

  // ─── Disconnected market codes (unlinked=true, non-main markets) ──────
  // These markets have their own independent data (not mirrored from main).
  const storeDisconnectedMarketCodes = useMemo(() => {
    return new Set(
      storeMarketsList
        .filter((m) => m.unlinked && !m.isMainMarket)
        .map((m) => Number.parseInt(m.value, 10)),
    );
  }, [storeMarketsList]);

  const onlineDisconnectedMarketCodes = useMemo(() => {
    return new Set(
      onlineMarketsList
        .filter((m) => m.unlinked && !m.isMainMarket)
        .map((m) => Number.parseInt(m.value, 10)),
    );
  }, [onlineMarketsList]);

  return {
    isDepartmentSelected,
    isSectionSelected,
    selectionBarState,
    markets,
    visibleStoreMarkets,
    visibleOnlineMarkets,
    storeConnectedMarketCodes,
    onlineConnectedMarketCodes,
    storeDisconnectedMarketCodes,
    onlineDisconnectedMarketCodes,
    storeMainMarketCode,
    onlineMainMarketCode,
    kpiSelection,
    isIncludeExcitementToggleChecked,
    isShowGroundArticlesToggleChecked,
    departmentMetadata,
    // Modal configs for rendering
    scaModalConfig,
    marketModalConfig,
    // Pending action state for DepartmentRegion to consume
    pendingScaAction,
    clearScaAction,
    pendingMarketAction,
    clearMarketAction,
    handleMarketLinkCompleted,
    handleSelectedMarketsChanged,
    // Handlers
    handleCoreExtendedChange,
    handleExcitementChange,
    handleIncludeExcitementToggleChange,
    handleShowGroundArticlesToggleChange,
    handleViewChange,
    handleTabChange,
  };
}
